import React from 'react';
import Seo from '../components/Seo';
import NewFooter from '../components/NewFooter';
import { buildOrganizationSchema, buildWebSiteSchema } from '../lib/structuredData';
import { buildSchemaGraph, formatPageTitle, shopUrl } from '../lib/seoUtils';
import HeroSection from '../components/HomePage/HeroSection';
import BlogSection from '../components/HomePage/BlogSection';
import TestimonialsSection from '../components/HomePage/TestimonialsSection';
import ContactSection from '../components/HomePage/ContactSection';
import MapSection from '../components/HomePage/MapSection';
import InfoCards from '../components/HomePage/InfoCards';
import '../components/HomePage/homepage.css';

const MISSION_IMG = '/assets/new_assets/mission-img.jpg';

/* ── ServicesIconsBar ─────────────────────────────────────── */
const iconItems = [
  { label: 'Blood Testing', icon: '/assets/icons/11.png' },
  { label: 'Weight Loss', icon: '/assets/icons/22.png' },
  { label: 'Consultations', icon: '/assets/icons/33.png' },
  { label: 'Prescriptions', icon: '/assets/icons/44.png' },
];

/* ── ServicesSection ──────────────────────────────────────── */
const services = [
  {
    title: 'Blood Test',
    mobileTagline: "Get accurate blood tests with doctor's report, sent as a PDF—easy, private, and discounted.",
    paragraphs: [
      "Our comprehensive blood test range offers accurate results, giving you the power to take control of your health. You can explore our <b>'A-Z list'</b> of specific tests or customise your own with the <b>'Build Your Package'</b> option, ensuring you get the most efficient and cost-effective solution with discounts on every additional test.",
      "We prioritise your convenience and privacy, making the blood testing process, as simple, easy and accessible as possible.",
      "All results are emailed directly to you as a PDF, complete with a detailed doctor’s report for easy interpretation, so you can make informed decisions about your health.",
    ],
    link: '/blood-testing',
    image: '/assets/new_assets/blood-test-home.png',
    reverse: false,
  },
  {
    title: 'Weight Loss',
    mobileTagline: 'Personalised, expert-guided weight loss with GLP-1 options like Wegovy—effective, safe, and fully supported.',
    paragraphs: [
      "Our weight loss service offers personalised solutions to help you achieve your health goals. With options like GLP-1 medications, including Wegovy and Mounjaro, we provide effective, medically supervised weight management tailored to your needs.",
      "Our team of experts will guide you through the process, offering ongoing support to ensure your success. We focus on delivering a practical and effective approach to weight loss, with regular check-ins and expert advice to support your progress.",
    ],
    link: '/weight-loss',
    image: '/assets/new_assets/blood-test-homes.webp',
    reverse: true,
  },
  {
    title: 'Travel Clinic',
    mobileTagline: 'We make it easy for you to get the right vaccinations and travel health advice.',
    paragraphs: [
      "We make it easy as we can for you to get the right vaccinations and travel health. During your consultation, our travel staff will check which vaccines and malaria prevention you need, this will depend on where you are going, which activities you have planned and your medical history.",
    ],
    link: '/travel-clinic',
    image: '/assets/new_assets/travel-clinic-home.webp',
    reverse: false,
  },
];


/* ── Page ─────────────────────────────────────────────────── */
const HomePage = () => (
  <>
    <Seo
      title={formatPageTitle("Private Pharmacy, Blood Tests & Travel Clinic")}
      description="London's trusted private pharmacy for fast blood testing, travel vaccinations, weight loss treatment and online prescriptions — care tailored to you."
      canonical={shopUrl("/")}
      url={shopUrl("/")}
      image={shopUrl("/assets/images/home-banner.png")}
      structuredData={buildSchemaGraph([
        buildOrganizationSchema(),
        buildWebSiteSchema(),
      ])}
    />
    <main>
      <HeroSection />

      {/* Services Icons Bar */}
      <div className="hp-icons-bar">
        <div className="hp-icons-bar__inner">
          {iconItems.map((item) => (
            <div key={item.label} className="hp-icons-bar__item">
              <img src={item.icon} alt={item.label} className="hp-icons-bar__icon" />
              <span className="hp-icons-bar__label">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Services Section */}
      <div id="our-services">
        {services.map((service) => (
          <div
            key={service.title}
            className={`hp-service-row${service.reverse ? ' hp-service-row--reverse' : ''}`}
          >
            <div
              className="hp-service-row__img"
              style={{ backgroundImage: `url(${service.image})` }}
              aria-hidden="true"
            />
            <div className="hp-service-row__body">
              <h2 className="hp-service-row__heading">{service.title}</h2>
              {service.mobileTagline && (
                <p className="hp-service-row__desc-mobile">{service.mobileTagline}</p>
              )}
              {service.paragraphs.map((p, i) => (
                <p key={i} className="hp-service-row__desc" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
              <a href={service.link} className="hp-service-row__btn">Read more</a>
            </div>
          </div>
        ))}
      </div>

      <BlogSection />

      {/* Mission Section */}
      <section className="hp-mission">
        <div className="hp-mission__img" style={{ backgroundImage: `url(${MISSION_IMG})` }} aria-hidden="true" />
        <div className="hp-mission__body">
          <h4 className="hp-mission__heading">
            Mission of Our<br />Healthcare House
          </h4>
          <p className="hp-mission__text">
            We look to deliver efficient, bespoke, and a customer-focused service
            with a friendly, trustworthy approach. Always going above and beyond.
          </p>
          <h2 className="hp-mission__signature">My Private Chemist</h2>
        </div>
      </section>

      <TestimonialsSection />
      <ContactSection />

      {/* Info Cards Section */}
      <InfoCards />

      <MapSection />
    </main>
    <NewFooter />
  </>
);

export default HomePage;
